import { getCurrentLocaleCode } from "@mongez/localization";
import { getPlatformTimeZone } from "./platform-time-zone.ts";

const arabicIndicDigits = "٠١٢٣٤٥٦٧٨٩";
const easternArabicIndicDigits = "۰۱۲۳۴۵۶۷۸۹";
const localizedDigitPattern = /[٠-٩۰-۹]/g;

/**
 * Select the active language's regional locale while forcing the Latin numbering system.
 *
 * @example
 * const locale = getLatinDigitFormattingLocale("ar");
 */
export function getLatinDigitFormattingLocale(
  localeCode = getCurrentLocaleCode(),
): string {
  return localeCode.toLowerCase().startsWith("ar")
    ? "ar-EG-u-nu-latn"
    : "en-US-u-nu-latn";
}

/**
 * Convert Arabic-Indic digit code points in a formatted value to ASCII digits.
 *
 * @example
 * normalizeLatinDigits("٢٠٢٦"); // "2026"
 */
export function normalizeLatinDigits(value: string): string {
  // Defensive: translated values may arrive as JSX arrays or numbers; never crash a page over a label.
  if (typeof value !== "string") {
    value = Array.isArray(value)
      ? (value as unknown[]).filter((part) => typeof part === "string" || typeof part === "number").join("")
      : String(value ?? "");
  }

  return value.replace(localizedDigitPattern, (digit) => {
    const arabicIndex = arabicIndicDigits.indexOf(digit);

    if (arabicIndex >= 0) return String(arabicIndex);

    return String(easternArabicIndicDigits.indexOf(digit));
  });
}

/**
 * Create a locale-aware number formatter that always emits Latin digits.
 *
 * @example
 * const formatter = createLatinNumberFormatter({ notation: "compact" });
 */
export function createLatinNumberFormatter(
  options: Intl.NumberFormatOptions = {},
  localeCode = getCurrentLocaleCode(),
): Intl.NumberFormat {
  return new Intl.NumberFormat(
    getLatinDigitFormattingLocale(localeCode),
    options,
  );
}

/**
 * Format a number in the active language while retaining ASCII digit glyphs.
 *
 * @example
 * formatLocalizedNumber(1250); // "1,250"
 */
export function formatLocalizedNumber(
  value: number,
  options: Intl.NumberFormatOptions = {},
  localeCode = getCurrentLocaleCode(),
): string {
  return normalizeLatinDigits(
    createLatinNumberFormatter(options, localeCode).format(value),
  );
}

/**
 * Create a locale-aware date formatter whose numeric fields use Latin digits.
 *
 * Without an explicit `timeZone` the platform timezone is used (not the
 * process' local zone), so SSR and the browser render the same text.
 *
 * @example
 * const formatter = createLatinDateTimeFormatter({ dateStyle: "medium" });
 */
export function createLatinDateTimeFormatter(
  options: Intl.DateTimeFormatOptions = {},
  localeCode = getCurrentLocaleCode(),
): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(getLatinDigitFormattingLocale(localeCode), {
    ...options,
    timeZone: options.timeZone ?? getPlatformTimeZone(),
  });
}

/**
 * Format a date in the active language while retaining ASCII digit glyphs.
 *
 * @example
 * formatLocalizedDateTime(Date.UTC(2026, 7, 24), { dateStyle: "medium" });
 */
export function formatLocalizedDateTime(
  value: number | Date,
  options: Intl.DateTimeFormatOptions = {},
  localeCode = getCurrentLocaleCode(),
): string {
  return normalizeLatinDigits(
    createLatinDateTimeFormatter(options, localeCode).format(value),
  );
}

/**
 * Format relative time in the active language while retaining ASCII digit glyphs.
 *
 * @example
 * formatLocalizedRelativeTime(-3, "day");
 */
export function formatLocalizedRelativeTime(
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  options: Intl.RelativeTimeFormatOptions = { numeric: "auto" },
  localeCode = getCurrentLocaleCode(),
): string {
  const formatter = new Intl.RelativeTimeFormat(
    getLatinDigitFormattingLocale(localeCode),
    options,
  );

  return normalizeLatinDigits(formatter.format(value, unit));
}
